import { Component, EventEmitter, Input, Output, forwardRef, OnInit, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, AbstractControl, NgControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-mat-input',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule
  ],
  templateUrl: './mat-input.component.html',
  styleUrl: './mat-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MatInputComponent),
      multi: true
    }
  ]
})
export class MatInputComponent implements ControlValueAccessor, OnInit {
  private defaultControl = new FormControl('');
  private _control: AbstractControl | null = null;

  constructor(private injector: Injector) {}

  @Input() set control(value: AbstractControl | null) {
    this._control = value || this.defaultControl;
    // Subscribe to value changes when control is set
    if (this._control) {
      this._control.valueChanges.subscribe(val => {
        if (val !== this.value) {
          this.value = val;
        }
      });
    }
  }
  get control(): FormControl {
    return (this._control as FormControl) || this.defaultControl;
  }

  @Input() label = '';
  @Input() placeholder = '';
  @Input() value: string = '';
  @Input() type: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' = 'text';
  @Input() disabled = false;
  @Input() required = false;
  @Input() readonly = false;
  @Input() appearance: 'fill' | 'outline' = 'fill';
  @Input() hint = '';
  @Input() errorMessage = '';
  private _customErrorMessages: string = '';
  @Input()
  set customErrorMessages(value: string) {
    this._customErrorMessages = value;
  }
  get customErrorMessages(): string {
    return this._customErrorMessages;
  }
  @Input() prefixIcon = '';
  @Input() prefixIconType: 'material' | 'png' = 'material';
  @Input() prefixIconSize = '22px';
  @Input() suffixIcon = '';
  @Input() showClearButton = true;
  @Input() minLength: number | null = null;
  @Input() maxLength: number | null = null;
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() pattern: string | RegExp | null = null;
  @Input() width = '100%';
  @Input() autocomplete = 'off';
  @Input() convertToUppercase = false;

  @Output() valueChange = new EventEmitter<string>();
  @Output() inputChange = new EventEmitter<Event>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() enter = new EventEmitter<void>();

  onChange = (_: any) => {};
  onTouched = () => {};

  writeValue(val: any): void {
    this.value = val;
    // Update the internal value when the form control value changes
    if (this._control && val !== this._control.value) {
      this._control.setValue(val, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: any) {
    let inputValue = event.target.value;

    // Convert to uppercase if the convertToUppercase flag is enabled
    if (this.convertToUppercase) {
      inputValue = inputValue.toUpperCase();
      // Update the input field value to show uppercase
      event.target.value = inputValue;
    }

    this.value = inputValue;
    this.onChange(this.value);
    this.onTouched();
    if (this._control) {
      this._control.setValue(this.value, { emitEvent: false });
    }
    this.valueChange.emit(this.value);
  }

  onValueChange(event: Event) {
    const input = event.target as HTMLInputElement;
    let inputValue = input.value;

    // Convert to uppercase if the convertToUppercase flag is enabled
    if (this.convertToUppercase) {
      inputValue = inputValue.toUpperCase();
      // Update the input field value to show uppercase
      input.value = inputValue;
    }

    this.value = inputValue;
    this.valueChange.emit(this.value);
    this.inputChange.emit(event);
  }

  onKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.enter.emit();
    }
  }

  clearValue() {
    this.value = '';
    this.onChange(this.value);
    this.onTouched();
    this.valueChange.emit('');
    if (this.control) {
      this.control.setValue('', { emitEvent: false });
    }
  }

    getErrorMessage(): string {
    if (!this._control) return '';
    const errors = this._control.errors;
    if (!errors) return '';

    // Check for custom error messages first
    if (this.errorMessage) {
      return this.errorMessage;
    }

    // Return custom error message if provided
    if (this.customErrorMessages) {
      return this.customErrorMessages;
    }

    // If no custom messages, return a default error message
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      return 'Invalid input';
    }

    return '';
  }

  hasError(): boolean {
    const hasError = !!this._control && this._control.errors !== null && (this._control.dirty || this._control.touched);
    return hasError;
  }

  ngOnInit() {
    // Try to get the control from NgControl if available
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl && ngControl.control) {
        this._control = ngControl.control;
        ngControl.valueAccessor = this;
      } else if (!this._control) {
        this._control = this.defaultControl;
      }
    } catch (error) {
      // If NgControl is not available, use default control
      if (!this._control) {
        this._control = this.defaultControl;
      }
    }
  }
}
